import { useState } from "react";
import type { ReactNode } from "react";
import {
  Box,
  AppBar,
  Drawer,
  Toolbar,
  List,
  CssBaseline,
  Divider,
  IconButton,
  Button,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Menu from "@mui/icons-material/Menu";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Key from "@mui/icons-material/Key";
import { Outlet } from "react-router-dom";
import CustomListItem from "./CustomListItem";
import type { NavigationItem } from "./CustomListItem";

export interface RootNavProps {
  setAuthenticated: (authenticated: boolean) => void | Promise<void>;
  elements?: ReactNode;
  menu: (NavigationItem | null | undefined | false)[];
  name: ReactNode;
  titleComponent?: "h1" | "h6";
  image?: string | false;
  passwordLink?: string | false;
  /** Existing clients keep their desktop layout; newer clients can enable the mobile drawer. */
  responsive?: boolean;
  logoutDisabled?: boolean;
}

/** Common layout for BOB, Trans and Plant. Uses APIs supported by Material UI 5 and 9. */
export default function RootNav({
  setAuthenticated,
  elements,
  menu,
  name,
  titleComponent = "h6",
  image = false,
  passwordLink = "/password",
  responsive = false,
  logoutDisabled = false,
}: RootNavProps) {
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const mobile = responsive && smallScreen;
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const expanded = mobile || open;
  const drawerWidth = expanded ? 200 : 64;
  const navigation = menu.filter((item): item is NavigationItem => !!item);
  const hasMenu = navigation.length > 0;
  const openDrawer = () => (mobile ? setMobileOpen(true) : setOpen(true));
  const closeDrawer = () => (mobile ? setMobileOpen(false) : setOpen(false));
  const shifted = !mobile && open && hasMenu;
  return (
    <Box
      sx={{
        display: "flex",
        position: "absolute",
        inset: 0,
        overflow: "hidden",
      }}
    >
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          zIndex: mobile ? theme.zIndex.appBar : theme.zIndex.drawer + 1,
          ml: shifted ? `${drawerWidth}px` : 0,
          width: shifted ? `calc(100% - ${drawerWidth}px)` : "100%",
        }}
      >
        <Toolbar
          variant="dense"
          sx={{ ...(image && !open ? { pl: "0 !important" } : {}), gap: 1 }}
        >
          {hasMenu &&
            (image && !open && !mobile ? (
              <Box
                component="button"
                aria-label="Navigation öffnen"
                onClick={openDrawer}
                sx={{
                  cursor: "pointer",
                  border: 0,
                  backgroundColor: "transparent",
                  backgroundImage: `url(${image})`,
                  height: 40,
                  width: 65,
                  mr: 2,
                  backgroundPosition: "center",
                  backgroundSize: "contain",
                  backgroundRepeat: "no-repeat",
                }}
              />
            ) : (
              <IconButton
                color="inherit"
                aria-label="Navigation öffnen"
                onClick={openDrawer}
                edge="start"
                sx={{ mr: 2, ...(shifted ? { visibility: "hidden" } : {}) }}
              >
                <Menu />
              </IconButton>
            ))}
          <Typography variant="h6" component={titleComponent} noWrap>
            {name}
          </Typography>
          <Box sx={{ flex: 1 }} />
          {elements}
          <Button
            color="inherit"
            disabled={logoutDisabled}
            onClick={() => void setAuthenticated(false)}
          >
            Abmelden
          </Button>
        </Toolbar>
      </AppBar>
      {hasMenu && (
        <Drawer
          variant={mobile ? "temporary" : "permanent"}
          open={mobile ? mobileOpen : true}
          onClose={closeDrawer}
          sx={{
            width: mobile ? 0 : drawerWidth,
            flexShrink: 0,
            whiteSpace: "nowrap",
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              overflowX: "hidden",
              backgroundColor: theme.palette.primary.dark,
              color: "white",
              border: 0,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              px: 1,
              minHeight: 48,
            }}
          >
            <IconButton
              aria-label="Navigation einklappen"
              sx={{ color: "white" }}
              onClick={closeDrawer}
            >
              {theme.direction === "rtl" ? <ChevronRight /> : <ChevronLeft />}
            </IconButton>
          </Box>
          <Divider />
          <List component="nav" aria-label="Hauptnavigation" sx={{ py: 0 }}>
            {navigation.map((item) => (
              <CustomListItem
                key={item.to}
                {...item}
                open={expanded}
                onNavigate={() => setMobileOpen(false)}
              />
            ))}
          </List>
          <Box sx={{ flex: 1 }} />
          {passwordLink && (
            <Box sx={{ flex: "0 0 auto" }}>
              <CustomListItem
                open={expanded}
                to={passwordLink}
                Icon={Key}
                text="Passwort ändern"
                onNavigate={() => setMobileOpen(false)}
              />
            </Box>
          )}
        </Drawer>
      )}
      <Box
        component="main"
        id="mainComponent"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          minHeight: 0,
          flexDirection: "column",
          display: "flex",
          overflow: "hidden",
          pt: "48px",
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
